// Passkeys (WebAuthn) — browser-side helpers.
//
// The backend sends challenges and credential metadata in URL-safe base64.
// The browser API expects ArrayBuffers. All the ugly conversion lives here.

import axios from "axios";
import api from "@/lib/api";

// Login routes (begin/complete) run BEFORE a token exists, so they must use
// bare axios with the absolute API URL. Authenticated routes (register,
// management) go through `api` so the axios interceptor stamps Authorization.
const API = `${import.meta.env.VITE_BACKEND_URL}/api`;

// ─── base64url <-> ArrayBuffer ────────────────────────────────────────────
function b64urlToBuffer(b64url) {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
  const bin = atob(b64 + pad);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

function bufferToB64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ─── Feature detection ────────────────────────────────────────────────────

export function passkeysSupported() {
  return !!(window.PublicKeyCredential && navigator.credentials);
}

export async function passkeysPlatformAvailable() {
  if (!passkeysSupported()) return false;
  try {
    // True on iOS (Face ID / Touch ID), Android (fingerprint / screen lock),
    // Mac with Touch ID, Windows Hello. False on headless/locked-down envs.
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// ─── Registration (user is already authenticated) ─────────────────────────

export async function registerPasskey() {
  if (!passkeysSupported()) throw new Error("passkeys_not_supported");

  const beginRes = await api.post("/auth/passkey/register/begin", {});
  const opts = beginRes.data;

  const publicKey = {
    challenge: b64urlToBuffer(opts.challenge),
    rp: opts.rp,
    user: {
      id: b64urlToBuffer(opts.user.id),
      name: opts.user.name,
      displayName: opts.user.displayName,
    },
    pubKeyCredParams: opts.pubKeyCredParams,
    timeout: opts.timeout,
    attestation: opts.attestation,
    authenticatorSelection: opts.authenticatorSelection,
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
      type: c.type,
      id: b64urlToBuffer(c.id),
    })),
  };

  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error("passkey_create_cancelled");

  const response = cred.response;
  const payload = {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bufferToB64url(response.attestationObject),
      clientDataJSON: bufferToB64url(response.clientDataJSON),
      // transports is optional; include when the authenticator reports it
      transports: response.getTransports ? response.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    authenticatorAttachment: cred.authenticatorAttachment || null,
  };

  const completeRes = await api.post("/auth/passkey/register/complete", payload);
  return completeRes.data;
}

// ─── Login (no auth required) ─────────────────────────────────────────────

// ─── Conditional mediation (autofill-driven passkey login) ────────────────
//
// When supported, the browser exposes available passkeys inline in the
// email field's autofill chip. The user taps the passkey suggestion and the
// credential flows directly through navigator.credentials.get() — no form
// submission, no password path, no email 2FA (because the server only
// triggers 2FA on the password login path).
//
// The call hangs in the background until either (a) the user picks a
// passkey, (b) caller aborts via the provided AbortController, or (c) the
// challenge expires on the backend. The component mounting this is
// responsible for aborting on unmount; otherwise the pending promise leaks.

export async function conditionalMediationAvailable() {
  if (!passkeysSupported()) return false;
  if (typeof window.PublicKeyCredential.isConditionalMediationAvailable !== "function") {
    return false;
  }
  try {
    return await window.PublicKeyCredential.isConditionalMediationAvailable();
  } catch {
    return false;
  }
}

/**
 * Start a conditional-mediation passkey flow. Returns a Promise that resolves
 * to { token, user } when the user picks a passkey, or rejects on abort/error.
 *
 * Caller MUST pass an AbortSignal and abort it on unmount.
 */
export async function startConditionalPasskeyLogin(signal) {
  if (!signal) throw new Error("signal required");

  const beginRes = await axios.post(
    `${API}/auth/passkey/login/begin`,
    {},
    { withCredentials: true }
  );
  const { challenge_id: challengeId, publicKey: pk } = beginRes.data;

  const publicKey = {
    challenge: b64urlToBuffer(pk.challenge),
    timeout: pk.timeout,
    rpId: pk.rpId,
    userVerification: pk.userVerification,
    allowCredentials: (pk.allowCredentials || []).map((c) => ({
      type: c.type,
      id: b64urlToBuffer(c.id),
    })),
  };

  const cred = await navigator.credentials.get({
    publicKey,
    mediation: "conditional",
    signal,
  });
  if (!cred) throw new Error("passkey_get_cancelled");

  const response = cred.response;
  const payload = {
    challenge_id: challengeId,
    credential: {
      id: cred.id,
      rawId: bufferToB64url(cred.rawId),
      type: cred.type,
      response: {
        authenticatorData: bufferToB64url(response.authenticatorData),
        clientDataJSON: bufferToB64url(response.clientDataJSON),
        signature: bufferToB64url(response.signature),
        userHandle: response.userHandle ? bufferToB64url(response.userHandle) : null,
      },
      clientExtensionResults: cred.getClientExtensionResults
        ? cred.getClientExtensionResults()
        : {},
    },
  };

  const completeRes = await axios.post(`${API}/auth/passkey/login/complete`, payload, {
    withCredentials: true,
  });
  return completeRes.data;
}

export async function loginWithPasskey() {
  if (!passkeysSupported()) throw new Error("passkeys_not_supported");

  const beginRes = await axios.post(
    `${API}/auth/passkey/login/begin`,
    {},
    { withCredentials: true, timeout: 15000 }
  );
  const { challenge_id: challengeId, publicKey: pk } = beginRes.data;

  const publicKey = {
    challenge: b64urlToBuffer(pk.challenge),
    timeout: pk.timeout,
    rpId: pk.rpId,
    userVerification: pk.userVerification,
    allowCredentials: (pk.allowCredentials || []).map((c) => ({
      type: c.type,
      id: b64urlToBuffer(c.id),
    })),
  };

  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error("passkey_get_cancelled");

  const response = cred.response;
  const payload = {
    challenge_id: challengeId,
    credential: {
      id: cred.id,
      rawId: bufferToB64url(cred.rawId),
      type: cred.type,
      response: {
        authenticatorData: bufferToB64url(response.authenticatorData),
        clientDataJSON: bufferToB64url(response.clientDataJSON),
        signature: bufferToB64url(response.signature),
        userHandle: response.userHandle ? bufferToB64url(response.userHandle) : null,
      },
      clientExtensionResults: cred.getClientExtensionResults
        ? cred.getClientExtensionResults()
        : {},
    },
  };

  const completeRes = await axios.post(`${API}/auth/passkey/login/complete`, payload, {
    withCredentials: true,
    timeout: 20000,
  });
  return completeRes.data; // { token, user }
}

// ─── Management (authenticated) ───────────────────────────────────────────

export async function listPasskeys() {
  const res = await api.get("/auth/passkey");
  return res.data;
}

export async function renamePasskey(id, deviceName) {
  const res = await api.put(`/auth/passkey/${id}`, { device_name: deviceName });
  return res.data;
}

export async function deletePasskey(id) {
  const res = await api.delete(`/auth/passkey/${id}`);
  return res.data;
}
